import React, {ChangeEvent} from "react";
import {Dropdown, DropdownItem, Label, Name} from "../../../components";
import RevocableTrustInputs from "./RevocableTrustInputs";
import {isFlowchartNameDuplicate, isValid} from "../../validation/ValidationUtils";
import {BASIC_DETAILS_CONSTANTS as Labels, VALIDATION_CONSTANTS as errors} from "../../constants/LabelConstants";
import {
    FlowchartModalInputs,
    ModalSource,
    RevocableTrustModalInputs,
    WillModalInputs
} from "src/EstateFlowchart/model/EstateFlowchartTypes";
import {EstateItemType} from "../../forms/EstateItemType";
import WillInputs from "./WillInputs";
import {useAppSelector} from "../../../store/hooks";
import {
    familyMembersSelector,
    getEstateFlowchartsSelector,
    getSelectedFlowchart
} from "../../slices/EstateFlowChartSlice";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";

type FlowchartModalContentProps = {
    modalSource: ModalSource,
    saveHasBeenClicked: boolean
    setFlowchartData: Function,
    modalInputs: FlowchartModalInputs,
}

const FlowchartModalContent: React.FC<FlowchartModalContentProps> = ({
                                                                         modalSource,
                                                                         modalInputs,
                                                                         setFlowchartData,
                                                                         saveHasBeenClicked
                                                                     }) => {

    const selectedFlowchart = useAppSelector(getSelectedFlowchart);
    const familyMembers = useAppSelector(familyMembersSelector);
    const estateFlowcharts = useAppSelector(getEstateFlowchartsSelector);
    const releaseToggles = useAppSelector(selectReleaseToggles);

    const canAddWill = () => {
        const {primaryWill, partnerWill} = selectedFlowchart?.willSummary || {};
        const isPartnerPresent = familyMembers.partnerMember;

        if (!primaryWill && !partnerWill) return true;
        if (primaryWill && partnerWill) return false;
        if (!isPartnerPresent) return !primaryWill;
        return true;
    };

    const createDropdownItems = () => {
        if (modalSource === ModalSource.AddFlowchart) {
            return Object.values(EstateItemType)
                .filter((estateItemType) =>{
                        if (releaseToggles?.enableWildCardInEstateFlowchart) {
                            return true;
                        } else {
                            return estateItemType !== EstateItemType.MISCELLANEOUS;
                        }
                    }
                )
                .map((estateItemType) => {
                    return createDropdownItem(estateItemType);
                });

        } else if (modalSource === ModalSource.AddEstateItem) {
            const dropdownItems: JSX.Element[] = [];
            if (selectedFlowchart === undefined || selectedFlowchart?.revocableTrust === null) {
                dropdownItems.push(createDropdownItem(EstateItemType.REVOCABLE_TRUST));
            }
            dropdownItems.push(createDropdownItem(EstateItemType.IRREVOCABLE_TRUST));
            if (canAddWill()) {
                dropdownItems.push(createDropdownItem(EstateItemType.WILL));
            }
            if (releaseToggles?.enableWildCardInEstateFlowchart) {
                dropdownItems.push(createDropdownItem(EstateItemType.MISCELLANEOUS));
            }
            return dropdownItems;
        }
    }

    const createDropdownItem = (estateItemType: EstateItemType) => {
        return <DropdownItem key={estateItemType} itemText={estateItemType.valueOf()} value={estateItemType.valueOf()}/>
    }

    const isDuplicate = () => {
        if (releaseToggles?.enableEstateFlowchartMVPRelease && isFlowchartNameDuplicate(estateFlowcharts, modalInputs?.flowchartName || "")) {
            return errors.DUPLICATE_FIELDS_FLOWCHART_NAME_TEXT;
        }
        return undefined;
    }

    return (
        <div className="textalign-left form-main flowchart-modal__content">
            {
                (modalSource === ModalSource.DuplicateFlowchart || modalSource === ModalSource.AddFlowchart) && <div className="flowchart-name-container">
                    <Name
                        name={modalInputs.flowchartName ?? null}
                        label={Labels.FLOWCHART_NAME_LABEL_TEXT}
                        aria-label={Labels.FLOWCHART_NAME_LABEL_TEXT}
                        className="layout-data-entry-form__field"
                        required={true}
                        forceShowErrors={saveHasBeenClicked}
                        placeholder={Labels.FLOWCHART_NAME_PLACEHOLDER_TEXT}
                        error={!isValid(modalInputs.flowchartName) ? errors.REQUIRED_FIELDS_FLOWCHART_NAME_TEXT : isDuplicate()}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const name = e?.target?.value.trimStart();
                            setFlowchartData((prev: any) => ({...prev, flowchartName: name}));
                        }}
                        autoComplete="off"
                        maxLength={60}
                    />
                    {!(saveHasBeenClicked && !isValid(modalInputs.flowchartName)) &&
                        <span>
                            {!(saveHasBeenClicked && isFlowchartNameDuplicate(estateFlowcharts, modalInputs?.flowchartName ?? "")) && <span> {Labels.FLOWCHART_NAME_SUB_TEXT} </span>}
                        </span>}
                </div>
            }
            {
                modalSource !== ModalSource.DuplicateFlowchart && <div className="estate-item-container" data-testid="estate-item-container">
                    <Label label={"Select Estate Item"} className="selectEstateItemLabel" required={true}
                           htmlFor={"estate-item"} labelId={"estate-item-label"}/>
                    <Dropdown
                        aria-label="Select Estate Item"
                        className="layout-data-entry-form__field"
                        error={saveHasBeenClicked && !isValid(modalInputs.estateItem) ? errors.REQUIRED_FIELDS_ESTATE_ITEM_TEXT : undefined}
                        defaultText="Select..."
                        value={modalInputs.estateItem}
                        onChange={(e) => {
                            setFlowchartData((prev: any) => ({...prev, estateItem: e?.value}));
                        }}>
                        {createDropdownItems()}
                    </Dropdown>
                </div>
            }

            {
                modalInputs.estateItem === EstateItemType.REVOCABLE_TRUST &&
                <RevocableTrustInputs setFormData={setFlowchartData}
                                      formData={modalInputs as RevocableTrustModalInputs}/>
            }

            {
                modalInputs.estateItem === EstateItemType.WILL &&
                <WillInputs setFormData={setFlowchartData} formData={modalInputs as WillModalInputs} modalSource={modalSource}/>
            }

        </div>
    );
};

export default FlowchartModalContent;